import { FC, ReactNode, useMemo } from "react";

import { Launcher } from "./Launcher/Launcher";
import { Panel } from "./Panel/Panel";

import type { AssistantFeature } from "../contexts/AssistantConfigContext";
import { AssistantWidgetProvider } from "../contexts/AssistantWidgetContext";

import type { useAssistantWidget } from "./helpers/useAssistantWidget";

type WidgetState = Pick<
  ReturnType<typeof useAssistantWidget>,
  "isOpen" | "hasOpened" | "open" | "close" | "requestClose" | "startNew" | "controls" | "launcherRef"
>;

interface WidgetProps {
  feature: AssistantFeature;
  widget: WidgetState;
  hidden?: boolean;
  details?: ReactNode;
  onCloseDetails?: () => void;
  children: ReactNode;
}

export const Widget: FC<WidgetProps> = ({ feature, widget, hidden = false, details, onCloseDetails, children }) => {
  const { close } = widget;
  const widgetContext = useMemo(() => ({ closePanel: () => close("navigation") }), [close]);
  return (
    <AssistantWidgetProvider value={widgetContext}>
      <Launcher
        ref={widget.launcherRef}
        feature={feature}
        isPanelOpen={widget.isOpen}
        isHidden={hidden}
        onClick={widget.open}
      />
      {widget.hasOpened && (
        <Panel
          feature={feature}
          isOpen={widget.isOpen}
          isHidden={hidden}
          isStartNewDisabled={!widget.controls || widget.controls.isBusy}
          onStartNew={widget.startNew}
          onClose={widget.requestClose}
          details={details}
          onCloseDetails={onCloseDetails}
        >
          {children}
        </Panel>
      )}
    </AssistantWidgetProvider>
  );
};
